import { useState } from 'react';
import { X } from 'lucide-react';
import type { CareActionType } from '@/types';

interface CareActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (actionType: CareActionType, notes?: string) => void;
  isSubmitting: boolean;
}

const careActions: { type: CareActionType; emoji: string; label: string }[] = [
  { type: 'WATERING', emoji: '💧', label: 'Полив' },
  { type: 'FERTILIZING', emoji: '🧪', label: 'Удобрение' },
  { type: 'MISTING', emoji: '💨', label: 'Опрыскивание' },
  { type: 'REPOTTING', emoji: '🪴', label: 'Пересадка' },
  { type: 'PRUNING', emoji: '✂️', label: 'Обрезка' },
  { type: 'ROTATING', emoji: '🔄', label: 'Поворот' },
  { type: 'CLEANING', emoji: '🧹', label: 'Чистка листьев' },
  { type: 'OTHER', emoji: '📝', label: 'Другое' },
];

export default function CareActionSheet({ isOpen, onClose, onSubmit, isSubmitting }: CareActionSheetProps) {
  const [selectedAction, setSelectedAction] = useState<CareActionType | null>(null);
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!selectedAction) return;
    onSubmit(selectedAction, notes.trim() || undefined);
    setSelectedAction(null);
    setNotes('');
  };

  const handleClose = () => {
    setSelectedAction(null);
    setNotes('');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center"
      style={{ background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
      onClick={handleClose}
    >
      <div className="glass-sheet w-full max-h-[85dvh] rounded-t-3xl flex flex-col animate-slide-up" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-4 border-b border-[var(--sf-border-light)] flex items-center justify-between flex-shrink-0">
          <h3 className="font-semibold text-lg">Записать уход</h3>
          <button
            onClick={handleClose}
            className="w-10 h-10 rounded-full glass-circle flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 min-h-0 px-4 py-4 space-y-4">
          {/* Action type grid */}
          <div className="grid grid-cols-3 gap-2">
            {careActions.map((action) => (
              <button
                key={action.type}
                onClick={() => setSelectedAction(action.type)}
                className={`p-3 rounded-xl text-center transition-all duration-200 ${
                  selectedAction === action.type
                    ? 'glass-circle-green'
                    : 'glass-circle'
                }`}
                style={selectedAction === action.type ? { borderColor: 'rgba(34, 197, 94, 0.5)' } : {}}
              >
                <span className="text-2xl block mb-1">{action.emoji}</span>
                <span className="text-xs font-medium">{action.label}</span>
              </button>
            ))}
          </div>

          {/* Notes */}
          {selectedAction && (
            <div>
              <label className="block text-sm font-medium mb-2">Заметка (необязательно)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Например: использовал удобрение Агрикола"
                className="tg-input"
              />
            </div>
          )}
        </div>

        {/* Submit button — pinned outside scroll area */}
        {selectedAction && (
          <div className="px-4 py-3 flex-shrink-0 safe-area-bottom">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-3 rounded-xl bg-plant-green-500 text-white font-medium transition-all active:scale-[0.98] disabled:opacity-70"
            >
              {isSubmitting ? 'Сохраняем...' : 'Записать'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
